import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../../../../../api/client';
import type { Perfil } from '../../../types';

interface UseEcadDragDropProps {
  catalog: Perfil[];
  setCatalog: React.Dispatch<React.SetStateAction<Perfil[]>>;
  selectedPerfilId: string;
  activePerfil: Perfil | null;
  loadCatalog: () => Promise<void>;
}

export function useEcadDragDrop({
  catalog,
  setCatalog,
  selectedPerfilId,
  activePerfil,
  loadCatalog,
}: UseEcadDragDropProps) {
  const [draggedPerfilId, setDraggedPerfilId] = useState<string | null>(null);
  const [draggedTopicoId, setDraggedTopicoId] = useState<string | null>(null);
  const [draggedAula, setDraggedAula] = useState<{ id: string; topicoId: string } | null>(null);

  // Drag & Drop de Perfis
  const handlePerfilDragStart = (e: React.DragEvent, id: string) => {
    setDraggedPerfilId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePerfilDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handlePerfilDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedPerfilId || draggedPerfilId === targetId) return;

    const list = [...catalog];
    list.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

    const dragIdx = list.findIndex(p => p.id === draggedPerfilId);
    const targetIdx = list.findIndex(p => p.id === targetId);
    if (dragIdx === -1 || targetIdx === -1) return;

    const [draggedItem] = list.splice(dragIdx, 1);
    list.splice(targetIdx, 0, draggedItem);

    const updatedList = list.map((item, index) => ({
      ...item,
      ordem: index + 1
    }));

    setCatalog(updatedList);

    try {
      toast.loading('Atualizando ordem dos perfis...', { id: 'perfil-reorder' });
      for (const item of updatedList) {
        const original = catalog.find(p => p.id === item.id);
        if (original && original.ordem !== item.ordem) {
          await api.post('/ecad/perfil', {
            id: item.id,
            nome: item.nome,
            descricao: (item as any).descricao || '',
            ordem: item.ordem,
            isInterno: item.isInterno
          });
        }
      }
      toast.success('Perfis reordenados com sucesso!', { id: 'perfil-reorder' });
      loadCatalog();
    } catch (err) {
      toast.error('Erro ao salvar nova ordem dos perfis.', { id: 'perfil-reorder' });
      loadCatalog();
    } finally {
      setDraggedPerfilId(null);
    }
  };

  // Drag & Drop de Módulos (Tópicos)
  const handleTopicoDragStart = (e: React.DragEvent, id: string) => {
    setDraggedTopicoId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTopicoDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleTopicoDrop = async (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedTopicoId || draggedTopicoId === targetId) return;

    const list = [...(activePerfil?.topicos || [])];
    const dragIdx = list.findIndex(t => t.id === draggedTopicoId);
    const targetIdx = list.findIndex(t => t.id === targetId);
    if (dragIdx === -1 || targetIdx === -1) return;

    const [draggedItem] = list.splice(dragIdx, 1);
    list.splice(targetIdx, 0, draggedItem);

    const updatedList = list.map((item, index) => ({
      ...item,
      ordem: index + 1
    }));

    const newCatalog = catalog.map(p => {
      if (p.id === selectedPerfilId) {
        return { ...p, topicos: updatedList };
      }
      return p;
    });
    setCatalog(newCatalog);

    try {
      toast.loading('Atualizando ordem dos módulos...', { id: 'topico-reorder' });
      for (const item of updatedList) {
        const original = activePerfil?.topicos.find(t => t.id === item.id);
        if (original && original.ordem !== item.ordem) {
          await api.post('/ecad/topico', {
            id: item.id,
            perfilId: selectedPerfilId,
            titulo: item.titulo,
            ordem: item.ordem
          });
        }
      }
      toast.success('Módulos reordenados com sucesso!', { id: 'topico-reorder' });
      loadCatalog();
    } catch (err) {
      toast.error('Erro ao salvar nova ordem dos módulos.', { id: 'topico-reorder' });
      loadCatalog();
    } finally {
      setDraggedTopicoId(null);
    }
  };

  // Drag & Drop de Aulas (Subtópicos)
  const handleAulaDragStart = (e: React.DragEvent, id: string, topicoId: string) => {
    setDraggedAula({ id, topicoId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleAulaDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleAulaDrop = async (e: React.DragEvent, targetId: string, topicoId: string) => {
    e.preventDefault();
    if (!draggedAula || draggedAula.topicoId !== topicoId || draggedAula.id === targetId) return;

    const topico = activePerfil?.topicos.find(t => t.id === topicoId);
    if (!topico) return;

    const list = [...(topico.subtopicos || [])];
    const dragIdx = list.findIndex(s => s.id === draggedAula.id);
    const targetIdx = list.findIndex(s => s.id === targetId);
    if (dragIdx === -1 || targetIdx === -1) return;

    const [draggedItem] = list.splice(dragIdx, 1);
    list.splice(targetIdx, 0, draggedItem);

    const updatedList = list.map((item, index) => ({
      ...item,
      ordem: index + 1
    }));

    const newCatalog = catalog.map(p => {
      if (p.id === selectedPerfilId) {
        return {
          ...p,
          topicos: p.topicos.map(t => {
            if (t.id === topicoId) {
              return { ...t, subtopicos: updatedList };
            }
            return t;
          })
        };
      }
      return p;
    });
    setCatalog(newCatalog);

    try {
      toast.loading('Atualizando ordem das aulas...', { id: 'aula-reorder' });
      for (const item of updatedList) {
        const original = topico.subtopicos.find(s => s.id === item.id);
        if (original && original.ordem !== item.ordem) {
          await api.post('/ecad/roteiro', {
            id: item.id,
            topicoId: topicoId,
            titulo: item.titulo,
            descricao: item.descricao || '',
            ordem: item.ordem,
            duracao: item.duracao,
            dificuldade: item.dificuldade,
            status: item.status,
            videoUrl: item.videoUrl,
            pdfUrl: item.pdfUrl
          });
        }
      }
      toast.success('Aulas reordenadas com sucesso!', { id: 'aula-reorder' });
      loadCatalog();
    } catch (err) {
      toast.error('Erro ao salvar nova ordem das aulas.', { id: 'aula-reorder' });
      loadCatalog();
    } finally {
      setDraggedAula(null);
    }
  };

  return {
    draggedPerfilId,
    draggedTopicoId,
    draggedAula,
    handlePerfilDragStart,
    handlePerfilDragOver,
    handlePerfilDrop,
    handleTopicoDragStart,
    handleTopicoDragOver,
    handleTopicoDrop,
    handleAulaDragStart,
    handleAulaDragOver,
    handleAulaDrop,
  };
}
